import { Grid } from '@material-ui/core';
import React, { FC, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import CopyIcon from 'images/applicant_detail/copy';
import EditIcon from 'images/applicant_detail/pen';
import DeleteIcon from 'images/applicant_detail/trashcan';
import globalMessages from 'shared/global/messages';

import { TestId } from '../constants';
import { useHoverActionStyles as useStyles } from '../styles';
import {
  ActionProps,
  ActionVariant,
  HoverAction,
  HoverActionProps,
} from './HoverAction';

const VariantMap: Record<
  'copy' | 'delete' | 'edit',
  Record<ActionVariant, React.ReactNode>
> = {
  copy: {
    icon: <CopyIcon />,
    text: <FormattedMessage {...globalMessages.copy} />,
  },
  delete: {
    icon: <DeleteIcon />,
    text: <FormattedMessage {...globalMessages.delete} />,
  },
  edit: {
    icon: <EditIcon />,
    text: <FormattedMessage {...globalMessages.edit} />,
  },
};

export interface HoverActionsProps {
  copyProps?: Partial<ActionProps>;
  deleteProps?: Partial<ActionProps>;
  editProps?: Partial<ActionProps>;
  variant?: HoverActionProps['variant'];
}

export const Bar: FC<{
  actionProps: Partial<ActionProps>;
  children?: React.ReactNode;
  defaultMessage: string;
  variant: ActionVariant;
}> = ({ actionProps, children, defaultMessage, variant }) => {
  const updatedActionProps = useMemo(
    () => ({
      ...actionProps,
      ariaLabel: actionProps?.ariaLabel ?? defaultMessage,
      onClick: actionProps?.onClick ?? (() => {}),
      tooltipTitle: actionProps?.tooltipTitle ?? defaultMessage,
      variant,
    }),
    [actionProps, defaultMessage, variant],
  );

  return <HoverAction {...updatedActionProps}>{children}</HoverAction>;
};

export const HoverActions: FC<HoverActionsProps> = ({
  copyProps,
  deleteProps,
  editProps,
  variant = 'icon',
}) => {
  const styles = useStyles();
  const intl = useIntl();
  return (
    <Grid
      alignItems="center"
      className={styles.actionsContainer}
      container
      data-testid={TestId.HOVER_ACTIONS}
      justify="center"
    >
      {[
        {
          actionProps: editProps,
          children: VariantMap.edit[variant],
          defaultMessage: intl.formatMessage(globalMessages.edit),
          testId: TestId.HOVER_ACTIONS_EDIT,
          variant,
        },
        {
          actionProps: copyProps,
          children: VariantMap.copy[variant],
          defaultMessage: intl.formatMessage(globalMessages.copy),
          testId: TestId.HOVER_ACTIONS_COPY,
          variant,
        },
        {
          actionProps: deleteProps,
          children: VariantMap.delete[variant],
          defaultMessage: intl.formatMessage(globalMessages.delete),
          testId: TestId.HOVER_ACTIONS_DELETE,
          variant,
        },
      ].map(
        ({ actionProps, testId, ...props }) =>
          actionProps && (
            <Grid
              className={styles.actionContainer}
              data-testid={testId}
              key={testId}
            >
              <Bar actionProps={actionProps} {...props} />
            </Grid>
          ),
      )}
    </Grid>
  );
};
